import airbrake from "utils/airbrake"
import units from "utils/units"
import { WindIcon } from "components/wind-icon"
import initSwellIcons from "components/swell-icon"
import { SnowAmount } from "components/snow-amount"
import { RainAmount } from "components/rain-amount"
import { TempValue } from "components/temp-value" // Snow and Mtn use the cold scale
import { TideTime } from "components/tide-time"

import { LevelValue } from "./level-value"
import ForecastTableHeader from "./header"
import { KEY_GRAPH } from "./data"

function insertRowBefore(cells, name, before) {
  let debug = false
  try {
    if (localStorage.getItem("fc-debug")) debug = true
  } catch (e) {}

  const rowCells = cells[name]
  if (!rowCells || !Array.isArray(rowCells)) {
    throw new Error(`Missing or invalid row "${name}" in expansion data`)
  }

  const parent = before.parentNode
  if (debug) {
    console.log(cells)
    console.log(name)
  }
  rowCells.forEach((td) => parent.insertBefore(td, before))
}

function shouldIgnoreRow(key) {
  return key === KEY_GRAPH
}

export default class ForecastTableDay {
  constructor({ table, date, minDetail, summaryCell }) {
    this.table = table
    this.date = date
    this.minDetail = minDetail
    this.summaryCell = summaryCell
    this.cells = {}
    this.headers = {}
    this.loading = false
  }

  size() {
    return this.currentCells("time").length
  }

  isIncomplete() {
    const cs = this.cellsFor({ detail: 0 })
    return !cs || !cs.time || cs.time.length < 3
  }

  expand() {
    const detail = Math.min(this.detail + 1, this.header.maxDetail)
    return this.changeDetail(detail)
  }

  collapse() {
    const detail = Math.max(this.minDetail, this.detail - 1)
    return this.changeDetail(detail)
  }

  insert(model, level, detail, cells, beforeDay) {
    this.setCells(model, level, detail, cells)
    this.insertCells(cells, beforeDay)
  }

  async change(model, level, detail) {
    if ((model === this.model && level === this.level && detail === this.detail) || this.loading) {
      return
    }

    this.loading = true

    try {
      let cells = this.cellsFor({ model, level, detail })
      if (!cells) {
        cells = await this.table.fetchOne(model, level, detail, this.date)
        if (!cells) throw new Error(`No data for ${this.date} at ${model}/${level}/${detail}`)
        this.setCells(model, level, detail, cells)
      }

      const previousDetail = this.detail
      this.updateCells(detail, cells)
      this.model = model
      this.level = level

      if (this.detail > previousDetail) this.table.onDetailIncrease?.(this.detail, level)
    } catch (e) {
      airbrake.notify(e)
    }

    this.loading = false
  }

  changeDetail(detail) {
    return this.change(this.model, this.level, detail)
  }

  changeLevel(level) {
    return this.change(this.model, level, this.detail)
  }

  changeModel(model) {
    return this.change(model, this.level, this.detail)
  }

  setCells(model, level, detail, cells) {
    if (!cells) return

    const dayHeaders = {}

    for (const key in cells) {
      if (shouldIgnoreRow(key)) continue

      if (key.match(/days\d+/)) {
        dayHeaders[key] = cells[key][0]
        continue
      }

      for (let i = 0; i < cells[key].length; i++) {
        const td = cells[key][i]

        // TODO: replace with proper inheritance
        if (
          window.initFCTableCell &&
          window.initFCTableCell(this, td, { model, level, detail, key, index: i })
        ) {
          continue
        }

        WindIcon.init(td)
        initSwellIcons(td, units)
        SnowAmount.init(td)
        RainAmount.init(td)
        TempValue.init(td)
        TideTime.init(td)
        LevelValue.init(td)
      }
    }

    this.setCellsFor(model, level, detail, cells)

    let css
    if (detail === 2) css = "is-expanded-h"
    if (detail === 1) css = "is-expanded-t"

    if (css && detail > this.minDetail) {
      for (const k in cells) {
        if (k === KEY_GRAPH) continue

        cells[k].forEach((cell) => cell.classList.add(css))
      }
    }

    if (this.model === undefined) this.model = model
    if (this.level === undefined) this.level = level

    if (this.detail === undefined) {
      this.refCells = cells
      this.detail = detail
      const header = cells.days[0]
      this.header = new ForecastTableHeader(header, this)
      this.headers.days = this.header

      for (const key in dayHeaders) {
        this.headers[key] = new ForecastTableHeader(dayHeaders[key], this)
      }
    }
  }

  insertCells(cells, beforeDay) {
    if (!cells || !cells.time) return

    const newSize = cells.time.length

    Object.keys(cells).forEach((name) => {
      if (shouldIgnoreRow(name)) return
      if (!Object.hasOwnProperty.call(cells, name)) return

      const before = beforeDay.firstCell(name)
      insertRowBefore(cells, name, before)
    })

    const freezingData = cells[KEY_GRAPH]
    this.table.detailChanged(this, 0, newSize, freezingData)
  }

  updateCells(detail, cells) {
    if (!cells || !cells.time) return

    const oldSize = this.size()
    const newSize = cells.time.length

    Object.keys(this.refCells).forEach((name) => {
      this.updateRow(cells, name, detail, newSize)
    })

    if (this.summaryCell) {
      this.summaryCell.colSpan += newSize - oldSize
    }

    const freezingData = cells[KEY_GRAPH]
    this.table.detailChanged(this, oldSize, newSize, freezingData)

    this.detail = detail
  }

  updateRow(cells, name, detail, size) {
    if (shouldIgnoreRow(name)) return

    if (name.match(/days\d*/)) {
      if (cells[name][0]) {
        this.headers[name].update(detail, size, cells[name][0])
      } else {
        this.removeRow(name)
      }
    } else {
      if (!cells[name]) {
        throw new Error(`Missing row "${name}" in new cells`)
      }
      const firstCell = this.firstCell(name)
      insertRowBefore(cells, name, firstCell)
      this.removeRow(name)
    }
  }

  currentCells(name) {
    return this.cellsFor()[name]
  }

  cellsFor({ model, level, detail } = {}) {
    const m = model ?? this.model
    const l = level ?? this.level
    const d = detail ?? this.detail
    if (!this.cells[m]) this.cells[m] = {}
    if (!this.cells[m][l]) this.cells[m][l] = []
    return this.cells[m][l][d]
  }

  setCellsFor(model, level, detail, cells) {
    this.cellsFor({ model, level, detail })
    this.cells[model][level][detail] = cells
  }

  firstCell(name) {
    if (name.match(/days\d*/)) return this.headers[name].root
    return this.currentCells(name)[0]
  }

  removeRow(name) {
    this.currentCells(name).forEach((td) => td.remove())
  }
}
